import { Flex, GapSize } from "@/components/atoms/flex";
import { useIntl } from "react-intl";
import {
	Button,
	Combobox,
	Field,
	Option,
	OptionGroup,
	Spinner,
	Text,
	type OptionOnSelectData,
	type SelectionEvents,
} from "@fluentui/react-components";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import type { InViewHookResponse } from "react-intersection-observer";
import {
	formatSipOrPhoneNumber,
	isValidPhoneNumber,
	isValidSip,
} from "@/common/helpers/validationHelper";

import { Validations } from "@/common/constants";
import { TransferChannelType } from "@/common/types/transfer";
import { type QueryResult, useInfiniteQueryHook } from "@/hooks/infiniteQuery";
import { ContactListItem } from "@/repository/models/contacts";
import {
	ContactsTransferKey,
	ContactsTransferTextKey,
	FavoritesTransferKey,
	FavoritesTransferTextKey,
	fetchContactsAgentsForTransfer,
	fetchContactsForTransfer,
	fetchFavoritesAgentsForTransfer,
	fetchFavoritesForTransfer,
} from "@/repository/queryFns/contacts";
import { AppSettingsState, CacheKey } from "@/common/stores/persistingStores/app/types";
import { useAppSettingsStore } from "@/common/stores/persistingStores/app/appSettingsStore";
import { PagedResult } from "@/repository/pagedResult";
import { ContactGroupType } from "../contacts/types";
import { AgentSearchDialogProps } from "./types";
import { useStyle } from "./style";

const transferInvalidInputTranslationKey = "Transfer.InvalidInput";

/** One thing the agent can transfer to: a contact carries up to three of them. */
interface TransferTarget {
	text: string;
	value: string;
	description: string;
	type: TransferChannelType;
}

export const AgentSearchDialog = (props: AgentSearchDialogProps) => {
	const intl = useIntl();
	const classes = useStyle();

	const [searchTarget, setSearchTarget] = useState<string>("");
	const [hasValidationError, setHasValidationError] = useState<boolean>(false);
	const [inputValidationMessage, setInputValidationMessage] = useState("");
	const [isSearchInitialized, setIsSearchInitialized] =
		useState<boolean>(false);

	const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			e.preventDefault();
			// Trigger action on Enter key
			handleAction();
		}
	};

	// Takes the value rather than the event, so every way the box can be filled runs the same rule: typing,
	// pasting, and picking a row all reach it. A paste never raises a change event, since the handler below
	// formats the text itself and cancels the default, and picking a row writes the value straight to state.
	const validateSearchInput = useCallback(
		(value: string) => {
			// Says what the limit is about. The generic max-length wording read as the search being refused,
			// while the search itself runs to four times this length and goes on returning matches.
			if (value.length > Validations.TransferTargetMaxLength) {
				setHasValidationError(true);
				setInputValidationMessage(
					intl.formatMessage(
						{ id: "Transfer.TargetMaxLength" },
						{ maxLength: Validations.TransferTargetMaxLength }
					)
				);
				return;
			}

			setHasValidationError(false);
			setInputValidationMessage("");
		},
		[intl]
	);

	// Validated while typing, so the message appears at the character that breaks the rule.
	const onSearchDestinationChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			setSearchTarget(event.target.value);
			validateSearchInput(event.target.value);
		},
		[setSearchTarget, validateSearchInput]
	);

	const onSearchDestinationBlur = useCallback(
		(event: React.FocusEvent<HTMLInputElement>) => {
			validateSearchInput(event.target.value);
		},
		[validateSearchInput]
	);

	const onSearchDestinationPaste: React.ClipboardEventHandler = useCallback(
		(event) => {
			const formatedValue = formatSipOrPhoneNumber(
				event.clipboardData.getData("text")
			);

			if(!formatedValue) {
				return;
			}

			event.preventDefault();
			setSearchTarget(formatedValue);
			validateSearchInput(formatedValue);
		},
		[setSearchTarget, validateSearchInput]
	);

	const onSearchDestinationSelect = useCallback(
		(_: SelectionEvents, data: OptionOnSelectData) => {
			const selected = data.optionValue ?? "";

			setSearchTarget(selected);
			validateSearchInput(selected);
		},
		[setSearchTarget, validateSearchInput]
	);

	const handleAction = useCallback(() => {
		if (!searchTarget) {
			setHasValidationError(true);
			setInputValidationMessage(
				intl.formatMessage({ id: "Transfer.TransferTargetRequired" })
			);
			return;
		}

		if (isSearchInputFormatValid()) {
			props.onAction(searchTarget);
		}
	}, [searchTarget, props.onAction]);

	const isSearchInputFormatValid = useCallback(() => {
		if (!isValidPhoneNumber(searchTarget) && !isValidSip(searchTarget)) {
			setHasValidationError(true);
			setInputValidationMessage(
				props.channelType === TransferChannelType.Voice
					? intl.formatMessage({ id: transferInvalidInputTranslationKey })
					: intl.formatMessage({ id: "Transfer.InvalidInputEmail" })
			);
			return false;
		}
		return true;
	}, [searchTarget, props.channelType, intl]);

	const appSettings = useAppSettingsStore(
		(state: AppSettingsState) => state.settings
	);

	const [favoritesResult, , favoritesInView, setFavoritesSearch] =
		useInfiniteQueryHook<ContactListItem>(
			props.channelType === TransferChannelType.Voice
				? FavoritesTransferKey
				: FavoritesTransferTextKey,
			props.channelType === TransferChannelType.Voice
				? fetchFavoritesForTransfer
				: fetchFavoritesAgentsForTransfer,
			CacheKey.ContactsTransferSearch,
			appSettings.favoritesIsEnabled && isSearchInitialized
		);

	const [contactsResult, , contactsInView, setContactsSearch] =
		useInfiniteQueryHook<ContactListItem>(
			props.channelType === TransferChannelType.Voice
				? ContactsTransferKey
				: ContactsTransferTextKey,
			props.channelType === TransferChannelType.Voice
				? fetchContactsForTransfer
				: fetchContactsAgentsForTransfer,
			CacheKey.ContactsTransferSearch,
			isSearchInitialized
		);

	// The dialog opens on an empty box, so it opens on an unfiltered list. The query stays off until that is
	// pushed through, because the hook seeds its search from the persisted cache and would otherwise open by
	// searching whatever was typed into the previous transfer.
	useEffect(() => {
		setFavoritesSearch("");
		setContactsSearch("");
		setIsSearchInitialized(true);
	}, [setFavoritesSearch, setContactsSearch]);

	const searchQuery = useMemo(() => searchTarget.trim(), [searchTarget]);

	// A search too long to be anything the agent is after is held back, since every word of it costs a wildcard
	// clause per field searched. The ceiling is the search's own, not the one the box accepts as a destination:
	// an address can be longer than a transfer target may be, and refusing to look for it would leave the agent
	// unable to reach a contact who is in the directory.
	const isSearchable = useMemo(
		() => searchQuery.length <= Validations.TransferSearchMaxLength,
		[searchQuery]
	);

	useEffect(() => {
		if (!isSearchable) {
			return;
		}

		const timerId = setTimeout(() => {
			setFavoritesSearch(searchQuery);
			setContactsSearch(searchQuery);
		}, 500);

		return () => clearTimeout(timerId);
	}, [searchQuery, isSearchable, setFavoritesSearch, setContactsSearch]);

	// A contact carries up to three targets and the dialog offers each one it can reach. The query returns
	// only contacts carrying at least one, so the empty-value filter below is the projection's own guard
	// rather than the thing that decides what the agent is shown.
	const buildTransferTargets = useCallback(
		(result: QueryResult<ContactListItem>): Array<TransferTarget> =>
			(result.data?.pages ?? [])
				.flatMap((page: PagedResult<ContactListItem>) => page.items)
				.flatMap((contact: ContactListItem) => {
					const description = [contact.jobTitle, contact.department]
						.filter((part: string) => !!part)
						.join(" / ");

					return [
						{
							text: contact.name,
							value: contact.userPrincipalName,
							description,
							type: TransferChannelType.Email,
						},
						{
							text: contact.name,
							value: contact.businessPhone,
							description,
							type: TransferChannelType.Voice,
						},
						{
							text: contact.name,
							value: contact.mobilePhone,
							description,
							type: TransferChannelType.Voice,
						},
					];
				})
				.filter(
					(target: TransferTarget) =>
						!!target.value &&
						(props.channelType === TransferChannelType.Voice ||
							target.type === TransferChannelType.Email)
				)
				.filter(
					(target: TransferTarget, index: number, self: Array<TransferTarget>) =>
						index ===
						self.findIndex(
							(other: TransferTarget) => other.value === target.value
						)
				),
		[props.channelType]
	);

	const favoriteTargets = useMemo(
		() => buildTransferTargets(favoritesResult),
		[buildTransferTargets, favoritesResult]
	);

	const contactTargets = useMemo(
		() => buildTransferTargets(contactsResult),
		[buildTransferTargets, contactsResult]
	);

	// The spinner at the end of a group is what asks for the next page: it is the element the hook watches, so
	// without it the dialog would offer the first page and nothing else, however many contacts matched.
	const buildOptionGroup = useCallback(
		(
			key: ContactGroupType,
			targets: Array<TransferTarget>,
			inView: InViewHookResponse,
			hasNextPage: boolean
		) => (
			<OptionGroup label={intl.formatMessage({ id: `Contacts.${key}` })}>
				{targets.map((target: TransferTarget) => (
					<Option
						key={target.value}
						text={target.value}
						value={target.value}
						checkIcon={null}
						className={classes.transferTargetOptionRow}
					>
						<Flex column className={classes.transferTargetOption}>
							<span className={classes.transferTargetOptionName}>
								{target.text}
							</span>
							<span>{target.value}</span>
							{!!target.description && (
								<span className={classes.transferTargetOptionDescription}>
									{target.description}
								</span>
							)}
						</Flex>
					</Option>
				))}
				{hasNextPage && (
					<Spinner
						ref={inView.ref}
						className={classes.spinner}
						size="tiny"
						label={intl.formatMessage({ id: "Common.Loading" })}
					/>
				)}
			</OptionGroup>
		),
		[intl, classes]
	);

	// Counted in targets rather than in contacts, and the two agree now that the query returns only contacts
	// the dialog can reach: a page that came back holds targets, so an empty pair of groups means the search
	// found nobody rather than a page the client threw away.
	const hasSearchResults = useMemo(
		() =>
			!searchQuery ||
			favoritesResult.isLoading ||
			contactsResult.isLoading ||
			favoriteTargets.length > 0 ||
			contactTargets.length > 0,
		[
			searchQuery,
			favoritesResult,
			contactsResult,
			favoriteTargets,
			contactTargets,
		]
	);

	const isSearchLoading = useMemo(
		() => favoritesResult.isLoading || contactsResult.isLoading,
		[favoritesResult, contactsResult]
	);

	const buildContactsComboBox = useMemo(
		() => (
			<Combobox
				freeform
				inlinePopup
				className={classes.transferTargetComboBox}
				autoComplete="none"
				placeholder={
					props.placeholder ||
					intl.formatMessage({
						id: `Transfer.${
							props.channelType === TransferChannelType.Voice
								? "InputPlaceholder"
								: "InputPlaceholderMessaging"
						}`,
					})
				}
				onChange={onSearchDestinationChange}
				onReset={onSearchDestinationChange}
				onPaste={onSearchDestinationPaste}
				onBlur={onSearchDestinationBlur}
				onOptionSelect={onSearchDestinationSelect}
				defaultValue={searchTarget}
				value={searchTarget}
				selectedOptions={[]}
				listbox={{ className: classes.listbox }}
				// The list may only sit above or below the input. Fluent's own fallbacks include the two
				// sides, so a list that stopped fitting below was thrown out to the right of the dialog,
				// which happens on every search because the list collapses to a spinner and back.
				// Fluent also turns autoSize on, which writes a max-height inline from the room around the
				// input. Inline beats the cap the listbox slot carries as a class, so the list grew to
				// whatever the window allowed; off, the cap is what decides its height. The popup still
				// takes the input's width, which matchTargetSize does and this does not touch.
				positioning={{
					position: "below",
					align: "start",
					fallbackPositions: ["above"],
					autoSize: false,
				}}
				input={{ onKeyDown: handleKeyPress }}
			>
				{/* Every branch below needs a searchable query, so without this the list would go blank with
				    nothing to say why the rows of the last search that ran have gone. */}
				{!isSearchable && (
					<Text className={classes.noResultText}>
						{intl.formatMessage({ id: "Transfer.SearchTooLong" })}
					</Text>
				)}
				{isSearchable && !hasSearchResults && (
					<Text className={classes.noResultText}>
						{intl.formatMessage({ id: "Contacts.SearchNoResults" })}
					</Text>
				)}
				{isSearchable && isSearchLoading && (
					<Spinner
						className={classes.spinner}
						size="tiny"
						label={intl.formatMessage({ id: "Common.Loading" })}
					/>
				)}
				{isSearchable &&
					hasSearchResults &&
					!isSearchLoading &&
					buildOptionGroup(
						ContactGroupType.Favorites,
						favoriteTargets,
						favoritesInView,
						!!favoritesResult.hasNextPage
					)}
				{isSearchable &&
					hasSearchResults &&
					!isSearchLoading &&
					buildOptionGroup(
						ContactGroupType.All,
						contactTargets,
						contactsInView,
						!!contactsResult.hasNextPage
					)}
			</Combobox>
		),
		[
			intl,
			classes,
			contactsResult,
			favoritesResult,
			contactTargets,
			favoriteTargets,
			contactsInView,
			favoritesInView,
			buildOptionGroup,
			searchTarget,
			isSearchable,
			isSearchLoading,
			hasSearchResults,
			onSearchDestinationBlur,
			onSearchDestinationChange,
			onSearchDestinationPaste,
			onSearchDestinationSelect,
			props.placeholder,
			props.channelType,
		]
	);

	const canTriggerAction = useMemo(() => {
		return !!searchTarget && !props.actionDisabled;
	}, [searchTarget, props.actionDisabled]);

	return (
		<Flex column className={classes.container}>
			{props.title && (
				<Flex hAlign="flex-start" className={classes.title}>
					{props.title}
				</Flex>
			)}
			<Field
				validationState={hasValidationError ? "error" : "none"}
				validationMessage={inputValidationMessage}
			>
				{buildContactsComboBox}
			</Field>
			<Flex gap={GapSize.Small} hAlign="flex-end">
				<Button onClick={props.onCancel}>
					{props.cancelButtonText ||
						intl.formatMessage({ id: "Common.Cancel" })}
				</Button>
				<Button
					title={props.actionDisabled ? props?.actionDisabledTooltip : ""}
					appearance="primary"
					onClick={handleAction}
					disabled={!canTriggerAction}
				>
					{props.actionButtonText ||
						intl.formatMessage({ id: "Transfer.ActionButton" })}
				</Button>
			</Flex>
		</Flex>
	);
};
